import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import {
    IOption,
    ISelectedOptionIds,
    sides,
    sidesUpperCase,
} from '../../../../models/product-detail';
import { findIcon, MATRIX_OPTIONS_ID } from '../../../common/constants';
import RowElementWrapper from '../../../common/row-element-wrapper';
import ToolTipAnchor from '../../../common/tooltip';
import SelectInput from '../../../inputs/select-input';
import { FormattedMessage, useIntl } from 'react-intl';
import FeatureFlagged from '../../../authorization/feature-flagged';
import * as CON from '../../../common/constants';
import { getGainId } from '../../../../hooks/gain-translate-hook';

interface IStarkeyChoiceProps {
    options: IOption[];
    selectedIds: ISelectedOptionIds;
    onChange: (rightSelectedIds: number[], leftSelectedIds: number[]) => void;
    side: string;
}

const StarkeyChoice = ({
    options,
    selectedIds,
    onChange,
    side,
}: IStarkeyChoiceProps) => {
    const intl = useIntl();
    const [gainOptions, setGainOptions] = useState<number[]>([]);
    const [ventOptions, setVentOptions] = useState<number[]>([]);

    const [gainChoiceLeft, setGainChoiceLeft] = useState<boolean>(false);
    const [gainChoiceRight, setGainChoiceRight] = useState<boolean>(false);

    useEffect(() => {
        if (selectedIds) {
            if (selectedIds.leftSelectedIds) {
                setGainChoiceLeft(
                    selectedIds.leftSelectedIds.some((x) =>
                        CON.gainMessageIDs.includes(x)
                    )
                );
            }
            if (selectedIds.rightSelectedIds) {
                setGainChoiceRight(
                    selectedIds.rightSelectedIds.some((x) =>
                        CON.gainMessageIDs.includes(x)
                    )
                );
            }
        }
    }, [selectedIds]);

    const earSides =
        side === 'B'
            ? [sides.right, sides.left]
            : side === 'L'
            ? [sides.left]
            : [sides.right];

    const updateSelectedIds = (event: React.ChangeEvent<HTMLInputElement>) => {
        const right = selectedIds.rightSelectedIds.concat([]);
        const left = selectedIds.leftSelectedIds.concat([]);

        switch (event.target.name) {
            case 'starkeyChoice':
                if (parseInt(event.target.value) === 782) {
                    right.push(parseInt(event.target.value));
                    left.push(parseInt(event.target.value));
                }
                if (parseInt(event.target.value) === 0) {
                    right.splice(right.indexOf(782), 1);
                    left.splice(left.indexOf(782), 1);
                }
                break;
            case `${sides.right}StarkeyChoiceGain`:
            case `${sides.left}StarkeyChoiceGain`:
                if (event.target.name.includes('Gain'))
                    options
                        .find((x) => x.id === 9)!
                        .items.map((y) => {
                            if (y.id === parseInt(event.target.value)) {
                                event.target.name.includes(sides.right)
                                    ? right.push(y.id)
                                    : left.push(y.id);
                            } else {
                                if (event.target.name.includes(sides.right)) {
                                    if (right.includes(y.id)) {
                                        right.splice(right.indexOf(y.id), 1);
                                    }
                                }
                                if (event.target.name.includes(sides.left)) {
                                    if (left.includes(y.id)) {
                                        left.splice(left.indexOf(y.id), 1);
                                    }
                                }
                            }
                        });
                break;
            case `${sides.right}StarkeyChoiceVent`:
            case `${sides.left}StarkeyChoiceVent`:
                if (event.target.name.includes('Vent'))
                    options
                        .find((x) => x.id === 11)!
                        .items.map((x) => {
                            if (x.id === parseInt(event.target.value)) {
                                event.target.name.includes(sides.right)
                                    ? right.push(x.id)
                                    : left.push(x.id);
                            } else {
                                if (event.target.name.includes(sides.right)) {
                                    if (right.includes(x.id)) {
                                        right.splice(right.indexOf(x.id), 1);
                                    }
                                }
                                if (event.target.name.includes(sides.left)) {
                                    if (left.includes(x.id)) {
                                        left.splice(left.indexOf(x.id), 1);
                                    }
                                }
                            }
                        });
                break;
            default:
        }
        onChange(right, left);
    };

    const isStarkeyChoice = (x: string) => {
        return (
            (side !== 'R' &&
                (selectedIds.leftSelectedIds.includes(parseInt(x)) ||
                    (x !== '782' &&
                        !selectedIds.leftSelectedIds.includes(782)))) ||
            (side !== 'L' &&
                (selectedIds.rightSelectedIds.includes(parseInt(x)) ||
                    (x !== '782' &&
                        !selectedIds.rightSelectedIds.includes(782))))
        );
    };

    const addMissingGainId = () => {
        const right = selectedIds.rightSelectedIds;
        const left = selectedIds.leftSelectedIds;

        const defaultGainOption = options.find(
            (option) => option.id === MATRIX_OPTIONS_ID
        )!.items[0].id;
        const remainingGainOptions = options
            .find((option) => option.id === MATRIX_OPTIONS_ID)!
            .items.filter((matrix) => matrix.id !== defaultGainOption)
            .map((matrix) => matrix.id)
            .toString();

        const isAvailableSide = options
            .find((option) => option.id === MATRIX_OPTIONS_ID)!
            .items.every((matrix) =>
                side === sidesUpperCase.right
                    ? matrix.isAvailableRight
                    : matrix.isAvailableLeft
            );
        const isDefaultGainOptionIncluded =
            side === sidesUpperCase.right
                ? right.includes(defaultGainOption)
                : left.includes(defaultGainOption);
        const isSomeRemaningGainOptionsIncluded =
            side === sidesUpperCase.right
                ? right.some((option) =>
                      remainingGainOptions.includes(option.toString())
                  )
                : left.some((option) =>
                      remainingGainOptions.includes(option.toString())
                  );

        if (
            isAvailableSide &&
            !isDefaultGainOptionIncluded &&
            !isSomeRemaningGainOptionsIncluded
        ) {
            side === sidesUpperCase.right
                ? right.push(defaultGainOption)
                : left.push(defaultGainOption);

            onChange(right, left);
        }
    };

    useEffect(() => {
        addMissingGainId();
    }, [
        side === sidesUpperCase.right
            ? selectedIds.rightSelectedIds.length
            : selectedIds.leftSelectedIds.length,
    ]);

    useEffect(() => {
        setGainOptions(options.find((y) => y.id === 9)!.items.map((x) => x.id));
        setVentOptions(
            options.find((y) => y.id === 11)!.items.map((x) => x.id)
        );
    }, [options]);
    return (
        <>
            {selectedIds.rightSelectedIds && selectedIds.leftSelectedIds && (
                <>
                    <RowElementWrapper>
                        <label>
                            {intl.formatMessage({
                                id: CON.DO_YOU_ID,
                                defaultMessage: CON.DO_YOU,
                            })}{' '}
                            <ToolTipAnchor
                                toolTipMessage={intl.formatMessage({
                                    id: CON.DO_YOU_TT_ID,
                                    defaultMessage: CON.DO_YOU_TT,
                                })}
                            />
                        </label>
                        <RowElementWrapper>
                            {['782', '0'].map((x) => {
                                return (
                                    <p
                                        style={{
                                            display: 'inline',
                                            margin: '0px 25px 0px 25px',
                                        }}
                                        key={
                                            x === '782'
                                                ? 'starkeyChoiceYes'
                                                : 'starkeyChoiceNo'
                                        }
                                    >
                                        <input
                                            type="Radio"
                                            name={'starkeyChoice'}
                                            id={x + 'StarkeyChoice'}
                                            value={x}
                                            checked={isStarkeyChoice(x)}
                                            onChange={updateSelectedIds}
                                        />
                                        <label htmlFor={x + 'StarkeyChoice'}>
                                            {x === '782'
                                                ? intl.formatMessage({
                                                      id: 'Yes',
                                                      defaultMessage: 'Yes',
                                                  })
                                                : intl.formatMessage({
                                                      id: 'No',
                                                      defaultMessage: 'No',
                                                  })}
                                        </label>
                                    </p>
                                );
                            })}
                            {!(
                                (side !== 'R' &&
                                    selectedIds.leftSelectedIds.includes(
                                        782
                                    )) ||
                                (side !== 'L' &&
                                    selectedIds.rightSelectedIds.includes(782))
                            ) && (
                                <>
                                    {earSides.map((side) => {
                                        return (
                                            <RowElementWrapper
                                                key={
                                                    side === sides.left
                                                        ? 'starkeyChoiceLeft'
                                                        : 'starkeyChoiceRight'
                                                }
                                            >
                                                <div className="col-sm-3">
                                                    <FontAwesomeIcon
                                                        icon={findIcon(
                                                            'fal',
                                                            'ear'
                                                        )}
                                                        style={{
                                                            color:
                                                                side ===
                                                                sides.left
                                                                    ? 'blue'
                                                                    : 'red',
                                                        }}
                                                    />{' '}
                                                    <p className="ear-mention">
                                                        {side === sides.left
                                                            ? intl.formatMessage(
                                                                  {
                                                                      id: 'Left_Ear',
                                                                      defaultMessage:
                                                                          'Left Ear',
                                                                  }
                                                              )
                                                            : intl.formatMessage(
                                                                  {
                                                                      id: 'Right_Ear',
                                                                      defaultMessage:
                                                                          'Right Ear',
                                                                  }
                                                              )}
                                                    </p>
                                                </div>
                                                <div className="col-sm-3">
                                                    <SelectInput
                                                        name={`${side}StarkeyChoiceGain`}
                                                        onChange={
                                                            updateSelectedIds
                                                        }
                                                        label="Gain"
                                                        options={Array.from(
                                                            new Set(
                                                                options
                                                                    .find(
                                                                        (y) =>
                                                                            y.id ===
                                                                            9
                                                                    )!
                                                                    .items.map(
                                                                        (x) => {
                                                                            if (
                                                                                side ===
                                                                                sides.left
                                                                                    ? x.isAvailableLeft
                                                                                    : x.isAvailableRight
                                                                            )
                                                                                return {
                                                                                    value: x.id,
                                                                                    text: x.name.includes(
                                                                                        '/'
                                                                                    )
                                                                                        ? x.name.split(
                                                                                              '/'
                                                                                          )[1]
                                                                                        : intl.formatMessage(
                                                                                              {
                                                                                                  id: getGainId(
                                                                                                      x.english
                                                                                                  ),
                                                                                                  defaultMessage:
                                                                                                      x.name,
                                                                                              }
                                                                                          ),
                                                                                };
                                                                        }
                                                                    )
                                                            )
                                                        )}
                                                        value={
                                                            side === sides.right
                                                                ? gainOptions!.filter(
                                                                      (x) =>
                                                                          selectedIds.rightSelectedIds.includes(
                                                                              x
                                                                          )
                                                                  )[0]
                                                                : gainOptions!.filter(
                                                                      (x) =>
                                                                          selectedIds.leftSelectedIds.includes(
                                                                              x
                                                                          )
                                                                  )[0]
                                                        }
                                                        labelClassName="col-sm-12"
                                                        fieldClassName="col-sm-12"
                                                        inlineStyle={{
                                                            backgroundColor:
                                                                '#f4f4f4',
                                                            height: '40px',
                                                            borderRadius: '0',
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-sm-3">
                                                    <SelectInput
                                                        name={`${side}StarkeyChoiceVent`}
                                                        onChange={
                                                            updateSelectedIds
                                                        }
                                                        label="Vent"
                                                        options={Array.from(
                                                            new Set(
                                                                options
                                                                    .find(
                                                                        (y) =>
                                                                            y.id ===
                                                                            11
                                                                    )!
                                                                    .items.map(
                                                                        (x) => {
                                                                            if (
                                                                                side ===
                                                                                sides.left
                                                                                    ? x.isAvailableLeft
                                                                                    : x.isAvailableRight
                                                                            )
                                                                                return {
                                                                                    value: x.id,
                                                                                    text: x.name,
                                                                                };
                                                                        }
                                                                    )
                                                            )
                                                        )}
                                                        value={
                                                            side === sides.right
                                                                ? ventOptions!.filter(
                                                                      (x) =>
                                                                          selectedIds.rightSelectedIds.includes(
                                                                              x
                                                                          )
                                                                  )[0]
                                                                : ventOptions!.filter(
                                                                      (x) =>
                                                                          selectedIds.leftSelectedIds.includes(
                                                                              x
                                                                          )
                                                                  )[0]
                                                        }
                                                        labelClassName="col-sm-12"
                                                        fieldClassName="col-sm-12"
                                                        inlineStyle={{
                                                            backgroundColor:
                                                                '#f4f4f4',
                                                            height: '40px',
                                                            borderRadius: '0',
                                                        }}
                                                    />
                                                </div>
                                                <>
                                                    {side === sides.left &&
                                                        gainChoiceLeft && (
                                                            <FeatureFlagged
                                                                features={[
                                                                    'Substitute 65 for 70 Gain Message',
                                                                ]}
                                                            >
                                                                <RowElementWrapper
                                                                    key={
                                                                        'starkeyChoiceLeftGainMsg'
                                                                    }
                                                                >
                                                                    <div className="col-sm-3">
                                                                        <span></span>
                                                                    </div>
                                                                    <div className="col-sm-6">
                                                                        <FormattedMessage id="Ear_Canal_Size_Permitting_65_Gain" />
                                                                    </div>
                                                                </RowElementWrapper>
                                                            </FeatureFlagged>
                                                        )}
                                                    {side === sides.right &&
                                                        gainChoiceRight && (
                                                            <FeatureFlagged
                                                                features={[
                                                                    'Substitute 65 for 70 Gain Message',
                                                                ]}
                                                            >
                                                                <RowElementWrapper
                                                                    key={
                                                                        'starkeyChoiceRightGainMsg'
                                                                    }
                                                                >
                                                                    <div className="col-sm-3">
                                                                        <span></span>
                                                                    </div>
                                                                    <div className="col-sm-6">
                                                                        <FormattedMessage id="Ear_Canal_Size_Permitting_65_Gain" />
                                                                    </div>
                                                                </RowElementWrapper>
                                                            </FeatureFlagged>
                                                        )}
                                                </>
                                            </RowElementWrapper>
                                        );
                                    })}
                                </>
                            )}
                        </RowElementWrapper>
                    </RowElementWrapper>
                </>
            )}
        </>
    );
};

export default StarkeyChoice;
